import React from 'react'
import {
  Plus, Search, Edit, Target, TrendingUp, Users,
  CheckCircle2, AlertCircle, X, Clock, Book, RefreshCw,
  BarChart3, Trash2
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { ObjectiveTemplate } from '@/services/api/templateService'

interface TemplateFormDialogProps {
  templateDialogOpen: boolean
  setTemplateDialogOpen: (open: boolean) => void
  editingTemplate: ObjectiveTemplate | null
  templateFormData: {
    name: string
    description: string
    template_type: string
    role_type: string
    objectives: any[]
  }
  setTemplateFormData: React.Dispatch<React.SetStateAction<{
    name: string
    description: string
    template_type: string
    role_type: string
    objectives: any[]
  }>>
  processing: boolean
  handleCloseTemplateDialog: () => void
  handleSaveTemplate: () => void
  // Template dialog filters
  templateDialogSearch: string
  setTemplateDialogSearch: (v: string) => void
  templateDialogKpaFilter: string
  setTemplateDialogKpaFilter: (v: string) => void
  templateDialogPillarFilter: string
  setTemplateDialogPillarFilter: (v: string) => void
  templateDialogStatusFilter: string
  setTemplateDialogStatusFilter: (v: string) => void
  resetTemplateDialogFilters: () => void
  // Available objectives
  availableObjectives: any[]
  loadingObjectives: boolean
  handleAddObjectiveToTemplate: (objective: any) => void
  handleRemoveObjectiveFromTemplate: (index: number) => void
  handleUpdateObjectiveFromLibrary: (index: number) => void
  handleRefreshAllFromLibrary: () => void
}

const TemplateFormDialog: React.FC<TemplateFormDialogProps> = ({
  templateDialogOpen,
  setTemplateDialogOpen,
  editingTemplate,
  templateFormData,
  setTemplateFormData,
  processing,
  handleCloseTemplateDialog,
  handleSaveTemplate,
  templateDialogSearch,
  setTemplateDialogSearch,
  templateDialogKpaFilter,
  setTemplateDialogKpaFilter,
  templateDialogPillarFilter,
  setTemplateDialogPillarFilter,
  templateDialogStatusFilter,
  setTemplateDialogStatusFilter,
  resetTemplateDialogFilters,
  availableObjectives,
  loadingObjectives,
  handleAddObjectiveToTemplate,
  handleRemoveObjectiveFromTemplate,
  handleUpdateObjectiveFromLibrary,
  handleRefreshAllFromLibrary,
}) => {
  return (
    <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {editingTemplate ? (
              <>
                <Edit className="h-5 w-5 text-blue-600" />
                Edit Template
              </>
            ) : (
              <>
                <Plus className="h-5 w-5 text-blue-600" />
                Create New Template
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {editingTemplate
              ? 'Update template details and modify the included objectives'
              : 'Create a template by selecting existing objectives from the database or defining new ones'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Template Basic Info */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="template-name">Template Name *</Label>
              <Input
                id="template-name"
                placeholder="e.g., Senior Lecturer Impact & Growth Template"
                value={templateFormData.name}
                onChange={(e) => setTemplateFormData({...templateFormData, name: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="template-description">Description</Label>
              <Textarea
                id="template-description"
                placeholder="Describe what this template is for and when it should be used..."
                value={templateFormData.description}
                onChange={(e) => setTemplateFormData({...templateFormData, description: e.target.value})}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="template-type">Template Type</Label>
                <select
                  id="template-type"
                  className="w-full border rounded-md p-2"
                  value={templateFormData.template_type}
                  onChange={(e) => setTemplateFormData({...templateFormData, template_type: e.target.value})}
                >
                  <option value="role_based">Role-Based</option>
                  <option value="institutional">Institutional</option>
                  <option value="generic">Generic</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="role-type">Role Type (optional)</Label>
                <Input
                  id="role-type"
                  placeholder="e.g., Lecturer, Administrator"
                  value={templateFormData.role_type}
                  onChange={(e) => setTemplateFormData({...templateFormData, role_type: e.target.value})}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Objectives Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-slate-900">Template Objectives</h4>
                <p className="text-sm text-slate-600">Select objectives from the institutional library to include in this template</p>
              </div>
              <Badge variant="outline">
                {templateFormData.objectives.length} objective{templateFormData.objectives.length !== 1 ? 's' : ''} added
              </Badge>
            </div>

            {/* Snapshot System Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-blue-900 space-y-1">
                  <p className="font-medium">Template Snapshot System</p>
                  {editingTemplate ? (
                    <p className="text-blue-800">
                      This template contains <strong>snapshots from {editingTemplate.created_at ? new Date(editingTemplate.created_at).toLocaleDateString() : 'creation'}</strong>. Library objectives may have changed since then. Use the <RefreshCw className="h-3 w-3 inline mx-0.5" /> buttons to update objectives to their latest versions before saving.
                    </p>
                  ) : (
                    <p className="text-blue-800">
                      When you add objectives, templates create <strong>immutable snapshots</strong> of the current library data. You're always adding the latest version, so no refresh is needed. Once saved, these snapshots remain unchanged unless explicitly refreshed during future edits.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Selected Objectives */}
            {templateFormData.objectives.length > 0 && (
              <div className="space-y-3 bg-slate-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-700">Selected Objectives:</p>
                  {editingTemplate && templateFormData.objectives.some((obj: any) => obj.library_objective_id || obj._sourceLibraryId || obj.id) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRefreshAllFromLibrary}
                      className="gap-2 text-xs"
                    >
                      <RefreshCw className="h-3 w-3" />
                      Refresh All from Library
                    </Button>
                  )}
                </div>
                {templateFormData.objectives.map((obj: any, index: number) => (
                  <div
                    key={index}
                    className={`flex items-start justify-between gap-4 bg-white rounded-lg p-3 border-2 ${
                      obj._wasUpdated ? 'border-green-200 bg-green-50' : 'border-slate-200'
                    }`}
                  >
                    <div className="flex-1 space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-slate-900">{obj.title || obj.name}</p>
                            {obj._wasUpdated && (
                              <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300 text-xs">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Updated
                              </Badge>
                            )}
                            {editingTemplate && (obj.library_objective_id || obj._sourceLibraryId || obj.id) && !obj._wasUpdated && (
                              <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 text-xs">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                {obj.id && !obj._sourceLibraryId && !obj.library_objective_id ? 'Old Snapshot - Consider Refreshing' : 'Snapshot from Creation'}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-600 mt-1">{obj.description}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap">
                        {obj.kpa?.display_name && (
                          <Badge variant="outline" className="text-xs">
                            <Book className="h-3 w-3 mr-1" />
                            {obj.kpa.display_name}
                          </Badge>
                        )}
                        {obj.kpis && obj.kpis.length > 0 && (
                          <Badge variant="outline" className="text-xs">
                            <BarChart3 className="h-3 w-3 mr-1" />
                            {obj.kpis.length} KPI{obj.kpis.length !== 1 ? 's' : ''}
                          </Badge>
                        )}
                        {obj._snapshotDate && (
                          <Badge variant="outline" className="text-xs text-slate-500">
                            <Clock className="h-3 w-3 mr-1" />
                            Snapshot: {new Date(obj._snapshotDate).toLocaleDateString()}
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-1 flex-shrink-0">
                      {editingTemplate && (obj.library_objective_id || obj._sourceLibraryId || obj.id) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUpdateObjectiveFromLibrary(index)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          title="Update from library"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveObjectiveFromTemplate(index)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Remove from template"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Available Objectives */}
            <div className="border rounded-lg p-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-700">Available Objectives from Database:</p>
                  {loadingObjectives && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                      Loading...
                    </div>
                  )}
                </div>

                {/* Filters */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 grid grid-cols-4 gap-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Search objectives..."
                        value={templateDialogSearch}
                        onChange={(e) => setTemplateDialogSearch(e.target.value)}
                        className="pl-9 h-9 text-sm"
                      />
                    </div>
                    <select
                      value={templateDialogKpaFilter}
                      onChange={(e) => setTemplateDialogKpaFilter(e.target.value)}
                      className="h-9 text-sm border border-slate-300 rounded-md px-3"
                    >
                      <option value="all">All KPAs</option>
                      {Array.from(new Set(availableObjectives.map(obj => obj.kpa?.display_name).filter(Boolean))).map(kpa => (
                        <option key={kpa} value={kpa}>{kpa}</option>
                      ))}
                    </select>
                    <select
                      value={templateDialogPillarFilter}
                      onChange={(e) => setTemplateDialogPillarFilter(e.target.value)}
                      className="h-9 text-sm border border-slate-300 rounded-md px-3"
                    >
                      <option value="all">All Pillars</option>
                      {Array.from(new Set(availableObjectives.map(obj => obj.pillar?.name).filter(Boolean))).map(pillar => (
                        <option key={pillar} value={pillar}>{pillar}</option>
                      ))}
                    </select>
                    <select
                      value={templateDialogStatusFilter}
                      onChange={(e) => setTemplateDialogStatusFilter(e.target.value)}
                      className="h-9 text-sm border border-slate-300 rounded-md px-3"
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="draft">Draft</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                  {(templateDialogSearch || templateDialogKpaFilter !== 'all' || templateDialogPillarFilter !== 'all' || templateDialogStatusFilter !== 'all') && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={resetTemplateDialogFilters}
                      className="h-9 shrink-0"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Clear
                    </Button>
                  )}
                </div>
              </div>

              <ScrollArea className="h-[400px] mt-4">
                {availableObjectives.length === 0 && !loadingObjectives ? (
                  <div className="text-center py-8 text-slate-500">
                    <Target className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                    <p>No objectives found in database</p>
                    <p className="text-xs mt-1">Objectives will appear here once they are created</p>
                  </div>
                ) : (
                  <div className="space-y-2 pr-4">
                    {availableObjectives
                      .filter(obj => {
                        // Already selected
                        if (templateFormData.objectives.some((selected: any) => selected.id === obj.id)) return false

                        // Search filter
                        if (templateDialogSearch && !obj.title?.toLowerCase().includes(templateDialogSearch.toLowerCase()) &&
                            !obj.description?.toLowerCase().includes(templateDialogSearch.toLowerCase())) {
                          return false
                        }

                        // KPA filter
                        if (templateDialogKpaFilter !== 'all' && obj.kpa?.display_name !== templateDialogKpaFilter) {
                          return false
                        }

                        // Pillar filter
                        if (templateDialogPillarFilter !== 'all' && obj.pillar?.name !== templateDialogPillarFilter) {
                          return false
                        }

                        // Status filter
                        if (templateDialogStatusFilter !== 'all' && obj.status !== templateDialogStatusFilter) {
                          return false
                        }

                        return true
                      })
                      .map((objective: any) => (
                        <div
                          key={objective.id}
                          className="flex items-start justify-between gap-4 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                          onClick={() => handleAddObjectiveToTemplate(objective)}
                        >
                          <div className="flex-1">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <p className="font-medium text-slate-900 text-sm">{objective.title || objective.name}</p>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50 shrink-0"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleAddObjectiveToTemplate(objective)
                                }}
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </div>
                            <p className="text-xs text-slate-600 mt-1 line-clamp-2">{objective.description}</p>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {objective.kpa && (
                                <Badge variant="outline" className="text-xs">
                                  <Target className="h-3 w-3 mr-1" />
                                  {objective.kpa.display_name}
                                </Badge>
                              )}
                              {objective.pillar && (
                                <Badge variant="outline" className="text-xs" style={{ backgroundColor: `${objective.pillar.color_code}20`, color: objective.pillar.color_code }}>
                                  {objective.pillar.name}
                                </Badge>
                              )}
                              {objective.status && (
                                <Badge variant="outline" className={`text-xs ${
                                  objective.status === 'active' ? 'bg-green-50 text-green-700' :
                                  objective.status === 'completed' ? 'bg-slate-50 text-slate-700' :
                                  'bg-amber-50 text-amber-700'
                                }`}>
                                  {objective.status}
                                </Badge>
                              )}
                              {objective.kpi_count > 0 && (
                                <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700">
                                  <TrendingUp className="h-3 w-3 mr-1" />
                                  {objective.kpi_count} KPI{objective.kpi_count > 1 ? 's' : ''}
                                </Badge>
                              )}
                              {objective.usage_stats && objective.usage_stats.total_instances > 0 && (
                                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">
                                  <Users className="h-3 w-3 mr-1" />
                                  {objective.usage_stats.total_instances} use{objective.usage_stats.total_instances > 1 ? 's' : ''}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCloseTemplateDialog}
            disabled={processing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveTemplate}
            disabled={processing || !templateFormData.name.trim() || templateFormData.objectives.length === 0}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {processing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                {editingTemplate ? 'Saving...' : 'Creating...'}
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                {editingTemplate ? 'Save Changes' : 'Create Template'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default TemplateFormDialog
